import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Code2, ExternalLink, Link2, Settings2, Info, CheckCircle2, ShieldCheck, Lock, Smartphone, QrCode, Activity, Calendar as CalendarIcon, Zap } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';

const RestApiGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
            className="flex items-center gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Button>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm shrink-0">
              <Code2 className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Panduan API Connector (Kran API)</h1>
              <p className="text-sm md:text-lg text-muted-foreground mt-1">
                Gunakan "Kran API" untuk menghubungkan AI dengan sistem eksternal secara real-time.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-primary/5 rounded-xl p-6 border border-primary/10 flex items-start gap-4">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Info className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed font-medium text-foreground">
              REST API Connector memungkinkan chatbot untuk memanggil endpoint API Anda setiap kali ada pertanyaan yang membutuhkan data terbaru secara real-time (misal: status pengiriman, stok barang, buat booking, atau info member).
            </p>
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <h4 className="font-bold text-amber-800 text-sm mb-1">Perbedaan dengan "Data Connector" di Menu Knowledge:</h4>
              <ul className="list-disc pl-5 text-amber-900 text-sm space-y-2">
                <li>
                  <strong className="text-amber-950">Data Connector (di menu Knowledge):</strong> Digunakan untuk menyedot semua data produk/artikel dari server Anda (satu arah) untuk disimpan dalam database pintar Chatbaze. AI sekadar membacanya sebagai memori saat menjawab pertanyaan umum. AI tidak bisa mengirim data atau aksi balik ke server Anda.
                </li>
                <li>
                  <strong className="text-amber-950">Kran API / API Connector (Di Halaman Ini):</strong> Digunakan untuk komunikasi dua arah (Live Tool Calling). AI bisa "mensubmit" data (seperti Form, POST data pelanggan, Booking) secara langsung ke Endpoint API Anda di tengah-tengah obrolan tanpa memindahkannya ke database Chatbaze.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Settings2 className="h-6 w-6 text-primary" />
              Langkah 1: Siapkan Endpoint API
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base">
                <p className="text-muted-foreground">Pastikan API Anda memenuhi kriteria berikut:</p>
                <ul className="list-disc pl-5 md:pl-6 space-y-2 text-muted-foreground">
                  <li>Dapat diakses secara publik atau melalui otentikasi.</li>
                  <li>Mengembalikan data dalam format <strong className="text-foreground">JSON</strong>.</li>
                  <li>Mendukung metode <strong className="text-foreground">GET</strong> atau <strong className="text-foreground">POST</strong>.</li>
                </ul>
                <div className="mt-4">
                  <p className="font-bold mb-2 text-foreground">Contoh Response yang Baik:</p>
                  <pre className="bg-slate-900 text-slate-100 p-3 md:p-4 rounded-md font-mono text-[10px] overflow-x-auto border max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`{
  "status": "success",
  "data": [
    { "id": 1, "nama": "Produk A", "stok": 50, "harga": "Rp 10.000" },
    { "id": 2, "nama": "Produk B", "stok": 12, "harga": "Rp 25.000" }
  ]
}`}</pre>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
              <Link2 className="h-6 w-6 text-primary" />
              Langkah 2: Konfigurasi di Chatbaze
            </h2>
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4 text-sm md:text-base">
                <ol className="list-decimal pl-5 md:pl-6 space-y-4 text-muted-foreground">
                  <li>Pilih <strong className="text-foreground">REST API</strong> pada pilihan tipe connector.</li>
                  <li>Masukkan <strong className="text-foreground">Base Endpoint URL</strong> website Anda.</li>
                  <li>Pilih <strong className="text-foreground">HTTP Method</strong> (biasanya GET).</li>
                  <li>Jika API Anda butuh token, masukkan di bagian <strong className="text-foreground">Headers</strong>:
                    <pre className="bg-muted p-2 rounded mt-2 text-[10px] md:text-xs font-mono border overflow-x-auto max-w-[260px] md:max-w-none mx-auto md:mx-0">
{`{
  "Authorization": "Bearer TOKEN_ANDA_DISINI",
  "Content-Type": "application/json"
}`}</pre>
                  </li>
                  <li>Klik <strong className="text-foreground">Simpan Connector</strong>.</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-6 pt-8 border-t">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-6 w-6 text-primary" />
            Otentikasi Basic Auth (Username & Password)
          </h2>
          
          <div className="space-y-4 text-sm md:text-base">
            <p className="text-muted-foreground">
              Untuk menggunakan Data Connector REST API dengan sistem seperti <strong className="text-foreground">Gowa</strong>, masukkan kredensial ke dalam kolom Headers dalam format JSON.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-3">
                  <CardTitle className="text-sm md:text-base flex items-center gap-2 text-foreground">
                    <Settings2 className="h-4 w-4 text-primary" />
                    1. Persiapan Kredensial
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Format: <code>Basic [Base64(username:password)]</code>. Cara termudah adalah dengan memasukkan header <strong className="text-foreground">Authorization</strong> secara langsung.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-3">
                  <CardTitle className="text-sm md:text-base flex items-center gap-2 text-foreground">
                    <Lock className="h-4 w-4 text-primary" />
                    2. Pengaturan di Chatbaze
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <ul className="list-disc pl-5 text-[11px] md:text-xs space-y-1 text-muted-foreground">
                    <li><strong>Base Endpoint URL</strong>: Contoh: <code>https://api.gowa.id/v1/data</code></li>
                    <li><strong>Headers</strong>: Masukkan JSON di bawah</li>
                  </ul>
                  <pre className="bg-slate-900 text-slate-100 p-3 rounded text-[10px] overflow-x-auto border max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`{
  "Authorization": "Basic [KODE_BASE64_ANDA]",
  "Content-Type": "application/json"
}`}</pre>
                </CardContent>
              </Card>
            </div>

            <Card className="border-primary/10 shadow-sm">
              <CardHeader className="bg-primary/5 py-3">
                <CardTitle className="text-base flex items-center gap-2 text-foreground">
                  <Code2 className="h-4 w-4 text-primary" />
                  3. Cara Mendapatkan Kode Base64
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Gabungkan <code>username:password</code> dan ubah ke Base64 menggunakan layanan online.
                </p>
                <div className="bg-muted p-4 rounded-lg space-y-3 border">
                  <ol className="list-decimal pl-5 text-xs space-y-2 text-muted-foreground">
                    <li>Buka layanan <strong><a href="https://www.base64encode.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Base64 Encode</a></strong>.</li>
                    <li>Masukkan <code>admin:rahasia123</code>.</li>
                    <li>Klik Encode. Hasil: <code>YWRtaW46cmFoYXNpYTEyMw==</code>.</li>
                    <li>Di Chatbaze, isi Header-nya menjadi:</li>
                  </ol>
                  <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto border max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`{
  "Authorization": "Basic YWRtaW46cmFoYXNpYTEyMw=="
}`}</pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-6 pt-8 border-t">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-6 w-6 text-primary" />
            Keamanan & Verifikasi (Tips Penting)
          </h2>
          
          <div className="space-y-6 text-sm md:text-base">
            <p className="text-muted-foreground leading-relaxed">
              Untuk menambahkan sistem keamanan yang bersifat umum (bisa dipakai oleh instansi maupun perusahaan mana pun), kuncinya ada pada <strong className="text-foreground">Desain Parameter</strong> dan <strong className="text-foreground">Instruksi AI</strong>.
            </p>

            <div className="grid grid-cols-1 gap-6">
              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    1. Metode "Dual-Key" (Parameter Ganda)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <p className="text-muted-foreground">
                    Ini adalah cara termudah dan paling umum. Jangan biarkan API bekerja hanya dengan 1 data yang bersifat publik.
                  </p>
                  <div className="bg-muted p-4 rounded-lg border space-y-3">
                    <p className="font-bold text-xs uppercase text-primary">Penerapan di Chatbaze:</p>
                    <ul className="list-disc pl-5 text-xs space-y-2 text-muted-foreground">
                      <li><strong>Contoh E-Commerce</strong>: Tambahkan parameter <code>id_pesanan</code> DAN <code>nomor_hp</code>.</li>
                      <li><strong>Contoh Perusahaan (HR)</strong>: Tambahkan parameter <code>id_karyawan</code> DAN <code>tanggal_lahir</code>.</li>
                      <li><strong>Contoh Sekolah</strong>: Tambahkan parameter <code>nisn</code> DAN <code>nama_ibu_kandung</code>.</li>
                    </ul>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    <strong>Logika di API Anda:</strong> Server Anda hanya akan memberikan data jika kedua data tersebut cocok di database. Jika salah satu salah, kirimkan pesan error: "Data tidak ditemukan atau tidak cocok".
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <Settings2 className="h-5 w-5 text-primary" />
                    2. Memanfaatkan "Deskripsi Fungsi" untuk Instruksi AI
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <p className="text-muted-foreground">
                    Di halaman Connector, ada kolom <strong>Deskripsi Fungsi</strong>. Gunakan kolom ini untuk memperketat perilaku AI sebelum melakukan pemanggilan API.
                  </p>
                  <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                    <p className="font-bold text-xs uppercase text-primary mb-2">Contoh Instruksi:</p>
                    <blockquote className="text-sm italic text-foreground border-l-4 border-primary pl-4 py-1">
                      "Gunakan alat ini untuk mengecek status pembayaran. Anda WAJIB menanyakan Nomor Tagihan dan 4 digit terakhir nomor telepon pelanggan sebelum memanggil API ini. Jangan pernah memberikan informasi jika salah satu data kosong."
                    </blockquote>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <Lock className="h-5 w-5 text-primary" />
                    3. Metode "Challenge-Response" (OTP)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4 text-muted-foreground">
                  <p>Jika datanya sangat sensitif (misal: data medis atau finansial), gunakan sistem 2-step:</p>
                  <ol className="list-decimal pl-5 text-xs space-y-2">
                    <li><strong>Langkah 1:</strong> AI menanyakan ID user. API hanya mengecek apakah ID itu ada, lalu mengirimkan kode rahasia ke Email/WhatsApp user yang terdaftar di sistem Anda.</li>
                    <li><strong>Langkah 2:</strong> AI berkata: "Saya sudah mengirimkan kode verifikasi ke email yang terdaftar. Silakan masukkan kodenya di sini."</li>
                    <li><strong>Langkah 3:</strong> User memasukkan kode, lalu AI memanggil API kedua untuk memverifikasi kode tersebut dan barulah memberikan datanya.</li>
                  </ol>
                </CardContent>
              </Card>

              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-4">
                  <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                    <Code2 className="h-5 w-5 text-primary" />
                    4. Melalui Integrasi Metadata (Untuk Developer)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4 text-muted-foreground">
                  <p>Jika chatbot Anda dipasang di website yang mengharuskan user Login, Anda bisa mengirimkan identitas user secara "gaib" lewat kode embed.</p>
                  <p className="text-xs">
                    <strong>Keuntungan:</strong> Chatbot tahu siapa yang sedang bertanya tanpa perlu bertanya lagi, dan API Anda bisa langsung memverifikasi hak akses user tersebut.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="bg-green-50 border border-green-200 p-6 rounded-xl space-y-2 mt-8">
              <h4 className="font-bold text-green-800 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" /> Saran Strategi Umum:
              </h4>
              <p className="text-sm text-green-700 leading-relaxed">
                Selalu gunakan prinsip <strong>"Minimum Necessary Data"</strong>. Jangan kirimkan semua data dari API ke AI. Kirimkan hanya ringkasan yang diperlukan saja untuk ditampilkan di chat. Dengan begitu, risiko kebocoran data sangat minim.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6 pt-8 border-t">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <Calendar className="h-6 w-6 text-primary" />
            Integrasi Google Calendar (Native API Layer)
          </h2>
          
          <div className="space-y-4 text-sm md:text-base">
            <p className="text-muted-foreground leading-relaxed">
              Ini adalah cara tercepat untuk menghubungkan AI dengan Google Calendar user masing-masing tanpa perlu kueri manual ke Google API. Gunakan endpoint lokal ini di <strong>API Connector</strong>.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-3">
                  <CardTitle className="text-sm md:text-base flex items-center gap-2 text-foreground">
                    <Zap className="h-4 w-4 text-primary" />
                    1. Cek Jadwal (Get Calendar)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <ul className="list-disc pl-5 text-[11px] md:text-xs space-y-1 text-muted-foreground">
                    <li><strong>Method</strong>: <code>POST</code></li>
                    <li><strong>URL</strong>: <code>/api/v1/calendar/get-calendar</code></li>
                  </ul>
                  <pre className="bg-slate-900 text-slate-100 p-3 rounded text-[10px] overflow-x-auto border">
{`{
  "website_id": "LIHAT_DI_HALAMAN_CONNECTORS",
  "secret_token": "TOKEN_ADMIN_ANDA",
  "timeMin": "[\"ISO_START_TIME\"]",
  "timeMax": "[\"ISO_END_TIME\"]"
}`}</pre>
                </CardContent>
              </Card>

              <Card className="border-primary/10 shadow-sm">
                <CardHeader className="bg-primary/5 py-3">
                  <CardTitle className="text-sm md:text-base flex items-center gap-2 text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    2. Buat Janji (Booking)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <ul className="list-disc pl-5 text-[11px] md:text-xs space-y-1 text-muted-foreground">
                    <li><strong>Method</strong>: <code>POST</code></li>
                    <li><strong>URL</strong>: <code>/api/v1/calendar/booking</code></li>
                  </ul>
                  <pre className="bg-slate-900 text-slate-100 p-3 rounded text-[10px] overflow-x-auto border">
{`{
  "website_id": "LIHAT_DI_HALAMAN_CONNECTORS",
  "secret_token": "TOKEN_ADMIN_ANDA",
  "summary": "Meeting: [\"NAMA_USER\"]",
  "start": "[\"WAKTU_Mulai\"]",
  "attendee_phone": "[\"NOMOR_HP\"]"
}`}</pre>
                </CardContent>
              </Card>
            </div>

            <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl space-y-4 mt-6">
              <h4 className="font-bold text-blue-800 flex items-center gap-2">
                <Info className="h-5 w-5" /> Tutorial Setup di Halaman Connectors
              </h4>
              
              <div className="space-y-6">
                {/* Tutorial Get Calendar */}
                <div>
                  <h5 className="font-bold text-sm text-blue-900 border-b border-blue-200/50 pb-2 mb-3">1. Konfigurasi Cek Jadwal (/get-calendar)</h5>
                  <ol className="list-decimal pl-5 text-sm text-blue-900 space-y-2 leading-relaxed">
                    <li>Pilih <strong>Method: POST</strong> dan masukkan Endpoint URL: <code>/api/v1/calendar/get-calendar</code></li>
                    <li>Biarkan tab <strong>Headers</strong> kosong.</li>
                    <li>Buka tab <strong>AI Parameters</strong>. Tambahkan 4 parameter berikut:</li>
                    <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px]">
                      <li><code>website_id</code> &rarr; Isi <strong>Fixed Value</strong> dengan ID Website Anda.</li>
                      <li><code>secret_token</code> &rarr; Isi <strong>Fixed Value</strong> dengan Token Rahasia Anda.</li>
                      <li><code>timeMin</code> &rarr; Kosongkan Fixed Value.</li>
                      <li><code>timeMax</code> &rarr; Kosongkan Fixed Value.</li>
                    </ul>
                  </ol>
                </div>

                {/* Tutorial Booking */}
                <div>
                  <h5 className="font-bold text-sm text-blue-900 border-b border-blue-200/50 pb-2 mb-3">2. Konfigurasi Buat Janji Temu (/booking)</h5>
                  <ol className="list-decimal pl-5 text-sm text-blue-900 space-y-2 leading-relaxed">
                    <li>Pilih <strong>Method: POST</strong> dan masukkan Endpoint URL: <code>/api/v1/calendar/booking</code></li>
                    <li>Biarkan tab <strong>Headers</strong> kosong.</li>
                    <li>Buka tab <strong>AI Parameters</strong>. Tambahkan parameter berikut:</li>
                    <ul className="list-disc pl-5 mt-2 space-y-1 text-[13px] grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                      <li><code>website_id</code> &rarr; Isi <strong>Fixed Value</strong>.</li>
                      <li><code>secret_token</code> &rarr; Isi <strong>Fixed Value</strong>.</li>
                      <li><code>summary</code> &rarr; Kosongkan Fixed Value.</li>
                      <li><code>start</code> &rarr; Kosongkan Fixed Value.</li>
                      <li><code>end</code> &rarr; Kosongkan Fixed Value.</li>
                      <li><code>attendee_name</code> &rarr; Kosongkan Fixed Value.</li>
                      <li><code>attendee_phone</code> &rarr; Kosongkan Fixed Value.</li>
                    </ul>
                  </ol>
                </div>

                <div className="bg-blue-100/50 p-4 rounded-lg mt-2 border border-blue-200">
                  <p className="text-[12px] font-medium text-blue-900 leading-relaxed">
                    <Info className="inline h-4 w-4 mb-0.5 mr-1" /> <strong>Catatan Tipe Data:</strong> Seluruh parameter di atas bertipe <strong>String (Teks)</strong>. Anda cukup mengetikkan isinya apa adanya di kolom Fixed Value (tanpa tambahan tanda kutip <code>"..."</code>). AI akan otomatis menyisipkan <code>website_id</code> dan <code>secret_token</code> saat menjalankan aksinya tanpa perlu menanyakannya ke pelanggan Anda!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 pt-8 border-t">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <Smartphone className="h-6 w-6 text-primary" />
            Integrasi Gowa (Device ID & Pairing)
          </h2>
          
          <div className="space-y-4 text-sm md:text-base">
            <Card className="border-primary/10 shadow-sm">
              <CardContent className="pt-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Masukkan <code>X-Device-Id</code> ke dalam kolom <strong>Headers</strong> bersama dengan kredensial login Anda:
                </p>
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-md font-mono text-xs overflow-x-auto border-l-4 border-primary shadow-inner max-w-[280px] md:max-w-none mx-auto md:mx-0">
{`{
  "Authorization": "Basic [KODE_BASE64_ANDA]",
  "X-Device-Id": "MASUKKAN_DEVICE_ID_ANDA_DISINI",
  "Content-Type": "application/json"
}`}</pre>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-primary/5 p-4 rounded-lg space-y-1 border border-primary/10">
                    <p className="text-xs font-bold flex items-center gap-1 text-foreground">
                      <Info className="h-3 w-3 text-primary" /> X-Device-Id
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Header khusus agar server Gowa mengenali perangkat yang melakukan panggilan API.
                    </p>
                  </div>
                  <div className="bg-primary/5 p-4 rounded-lg space-y-1 border border-primary/10">
                    <p className="text-xs font-bold flex items-center gap-1 text-foreground">
                      <Smartphone className="h-3 w-3 text-primary" /> device_id
                    </p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      ID yang didapatkan dari Step 1 (proses pembuatan device di Gowa).
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-amber-100 bg-amber-50/30 shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-amber-700">
                    <Activity className="h-4 w-4" />
                    WebSocket vs REST
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[11px] leading-relaxed text-amber-800">
                    Chatbaze menggunakan <strong>HTTP REST</strong>. Abaikan WebSocket URL, cukup fokus pada Step 2 (REST calls).
                  </p>
                </CardContent>
              </Card>

              <Card className="border-green-100 bg-green-50/30 shadow-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-green-700">
                    <QrCode className="h-4 w-4" />
                    Penting: Pairing
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[11px] leading-relaxed text-green-800">
                    Selesaikan <strong>Step 3 (Pairing QR/Code)</strong> di Gowa agar perangkat aktif sebelum chatbot mengambil data.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            Tips AI
          </h2>
          <Card className="border-primary/5 bg-primary/5 shadow-none">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI kami akan mencoba memahami struktur JSON Anda secara otomatis. Namun, memberikan data yang rapi dan terstruktur akan sangat membantu AI memberikan jawaban yang lebih akurat kepada user Anda.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="pt-8 flex flex-col items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Button>
          <p className="text-xs text-muted-foreground">
            Butuh bantuan lebih lanjut? Hubungi tim support kami melalui chat di pojok kanan bawah.
          </p>
      </div>
    </div>
  );
};

export default RestApiGuide;
